
import React, { useState, useEffect } from 'react';
import { AppSettings } from '../types';
import { Settings, X, Check, Key, Cpu, Activity, Globe, Info, Server, RotateCcw } from 'lucide-react';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: AppSettings;
  onSave: (newSettings: AppSettings) => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, settings, onSave }) => {
  const [formData, setFormData] = useState<AppSettings>(settings);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    if (isOpen) setFormData(settings);
  }, [settings, isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(formData);
    setIsSaved(true);
    setTimeout(() => { setIsSaved(false); onClose(); }, 800);
  };

  const modelOptions = formData.apiProvider === 'grsai' ? [
    { id: 'gemini-3-pro-image-preview', name: 'Banana Pro (Grsai)', desc: '高质量节点，支持 4K 及多种比例' },
    { id: 'gemini-2.5-flash-image', name: 'Banana Fast (Grsai)', desc: '极速生成，适合快速迭代' }
  ] : [
    { id: 'gemini-3-pro-image-preview', name: 'Gemini 3.0 Pro Image', desc: '官方旗舰模型，细节卓越' },
    { id: 'gemini-2.5-flash-image', name: 'Gemini 2.5 Flash Image', desc: '平衡速度与质量' }
  ];

  const activeKeyField = formData.apiProvider === 'google' ? 'googleApiKey' : formData.apiProvider === 'grsai' ? 'grsaiApiKey' : 'siliconflowApiKey';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
      <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-md shadow-2xl overflow-hidden flex flex-col">
        
        <div className="flex items-center justify-between p-4 border-b border-slate-800 bg-slate-900/50">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-indigo-400" />
            <h3 className="text-lg font-bold text-white">接口与模型配置</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-full text-slate-400 transition-colors"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700">
            
            {/* Provider Selection */}
            <div className="space-y-3">
                <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-2"><Globe size={14} /> 供应商选择</label>
                <div className="grid grid-cols-3 gap-2">
                    {['google', 'grsai', 'siliconflow'].map((p) => (
                        <button 
                            key={p}
                            onClick={() => setFormData({...formData, apiProvider: p as any})}
                            className={`py-2 px-1 rounded-xl border text-[10px] font-bold transition-all ${formData.apiProvider === p ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg' : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'}`}
                        >
                            {p.toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>

            {/* Custom Host for Google (Official/Proxy) */}
            {formData.apiProvider === 'google' && (
                <div className="space-y-3 p-3 bg-slate-950/50 rounded-xl border border-slate-800">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-bold text-slate-400 flex items-center gap-2"><Server size={14} /> 自定义 Base URL</label>
                      <button 
                        onClick={() => setFormData({...formData, googleBaseUrl: ''})}
                        className="text-[9px] text-indigo-400 hover:text-white flex items-center gap-1 hover:bg-indigo-500/20 px-1.5 py-0.5 rounded transition-colors"
                        title="重置为官方默认"
                      >
                        <RotateCcw size={10} /> 恢复默认
                      </button>
                    </div>
                    <div className="flex flex-col gap-2">
                        <input 
                            type="text"
                            value={formData.googleBaseUrl || ''}
                            onChange={(e) => setFormData({...formData, googleBaseUrl: e.target.value})}
                            placeholder="默认 (官方): https://generativelanguage.googleapis.com"
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs text-indigo-300 focus:border-indigo-500 outline-none placeholder-slate-600"
                        />
                        <p className="text-[9px] text-slate-500">仅用于对接中转 API，留空则使用 Google 官方服务。</p>
                    </div>
                </div>
            )}

            {/* Custom Host for Grsai */}
            {formData.apiProvider === 'grsai' && (
                <div className="space-y-3 p-3 bg-slate-950/50 rounded-xl border border-slate-800">
                    <label className="text-xs font-bold text-slate-400 flex items-center gap-2"><Server size={14} /> Grsai 自定义 BaseURL</label>
                    <div className="flex flex-col gap-2">
                        <input 
                            type="text"
                            value={formData.grsaiHost}
                            onChange={(e) => setFormData({...formData, grsaiHost: e.target.value})}
                            placeholder="https://api.example.com"
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs text-indigo-300 focus:border-indigo-500 outline-none"
                        />
                        <div className="flex gap-2">
                            <button onClick={() => setFormData({...formData, grsaiHost: 'https://grsai.dakka.com.cn'})} className="text-[9px] bg-slate-800 hover:bg-slate-700 px-2 py-1 rounded text-slate-400">国内备用</button>
                            <button onClick={() => setFormData({...formData, grsaiHost: 'https://grsaiapi.com'})} className="text-[9px] bg-slate-800 hover:bg-slate-700 px-2 py-1 rounded text-slate-400">海外节点</button>
                        </div>
                    </div>
                </div>
            )}

            {/* API Key Input */}
            <div className="space-y-3">
                <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-2"><Key size={14} /> API KEY</label>
                <input 
                    type="password"
                    placeholder={`输入 ${formData.apiProvider} 的密钥...`}
                    value={formData[activeKeyField as keyof AppSettings] as string || ''}
                    onChange={(e) => setFormData({...formData, [activeKeyField]: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-700 focus:border-indigo-500 outline-none transition-all"
                />
                <p className="text-[10px] text-slate-500 px-1 italic flex items-center gap-1"><Info size={10} /> 密钥将本地存储在您的浏览器中</p>
            </div>

            {/* Model Selector */}
            <div className="space-y-3">
                <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-2"><Cpu size={14} /> 基础生成模型</label>
                <div className="grid grid-cols-1 gap-2">
                    {modelOptions.map((option) => (
                      <label 
                        key={option.id}
                        className={`flex items-center p-3 rounded-xl border cursor-pointer transition-all ${
                          formData.imageModel === option.id ? 'bg-indigo-600/10 border-indigo-500' : 'bg-slate-950 border-slate-800 hover:border-slate-700'
                        }`}
                      >
                        <input type="radio" className="hidden" checked={formData.imageModel === option.id} onChange={() => setFormData({...formData, imageModel: option.id as any})} />
                        <div className="flex flex-col">
                            <span className="text-sm font-bold text-slate-200">{option.name}</span>
                            <span className="text-[10px] text-slate-500">{option.desc}</span>
                        </div>
                        {formData.imageModel === option.id && <Check size={16} className="ml-auto text-indigo-400" />}
                      </label>
                    ))}
                </div>
            </div>

            {/* Concurrency */}
            <div className="space-y-3">
                <div className="flex justify-between items-center">
                    <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-2"><Activity size={14} /> 并发线程数</label>
                    <span className="text-xs font-mono text-indigo-400 font-bold">{formData.concurrency}</span>
                </div>
                <input type="range" min="1" max="50" value={formData.concurrency} onChange={e => setFormData({...formData, concurrency: parseInt(e.target.value)})} className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-indigo-500" />
            </div>

        </div>

        <div className="p-4 border-t border-slate-800 bg-slate-900/80 flex justify-end">
            <button onClick={handleSave} className={`px-8 py-2 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${isSaved ? 'bg-emerald-600 text-white' : 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-lg shadow-indigo-900/20'}`}>
                {isSaved ? <Check size={18} /> : null}
                {isSaved ? '配置已更新' : '保存配置'}
            </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
